import { h, Component } from 'preact';
import debounce from 'lodash.debounce';
import produce, { Draft } from 'immer';
import classNames from 'classnames';
import SendIcon from 'widget_main/globals/assets/send.svg';
import Button from 'widget_main/components/Button';
import TextArea from 'widget_main/components/TextArea';
import { Localizer, Text } from 'preact-i18n';
import { getKustomerCore } from 'widget_main/globals/helpers';
import { MessageThreadInputProps, MessageThreadInputState } from './types';
import AttachmentPreviews from './AttachmentPreviews';
import AttachmentButton from './AttachmentButton';
import styles from './messageThreadInput.scss';
import messages from './messages';

const TEXT_AREA_ID = 'messageThreadInputTextArea';
let textAreaElement;

const kustomerCore = getKustomerCore();

class MessageThreadInput extends Component<
  MessageThreadInputProps,
  MessageThreadInputState
> {
  constructor() {
    super();
    this.state = {
      body: '',
      attachments: [],
      errorMessage: undefined,
    };

    this.debouncedUserTyping = debounce(this.userTyping, 1000, {
      leading: true,
      maxWait: 2500,
    });

    this.debouncedUserStoppedTyping = debounce(this.userStoppedTyping, 5000);
  }

  debouncedUserTyping: () => void;

  debouncedUserStoppedTyping: () => void;

  focusOnTextArea() {
    textAreaElement = document.getElementById(TEXT_AREA_ID);

    if (textAreaElement) textAreaElement.focus();
  }

  componentDidUpdate(prevProps) {
    const { isInputHidden: prevIsInputHidden } = prevProps;
    const { isInputHidden } = this.props;

    if (prevIsInputHidden === true && isInputHidden === false) {
      this.focusOnTextArea();
    }
  }

  componentDidMount() {
    this.focusOnTextArea();
  }

  componentWillUnmount() {
    const { attachments } = this.state;
    attachments.forEach((attachment) => {
      if (attachment.url) URL.revokeObjectURL(attachment.url);
    });
  }

  resetTextAreaSize = () => {
    textAreaElement.style.height = 0;
    textAreaElement.style.height = `${textAreaElement.scrollHeight}px`;
  };

  showError = (errorMessage) => {
    this.setState(
      produce((draftState: Draft<MessageThreadInputState>) => {
        draftState.errorMessage = errorMessage;
      }),
    );
  };

  addAttachment = (file) => {
    const previewUrl = URL.createObjectURL(file);

    this.setState(
      produce((draftState: Draft<MessageThreadInputState>) => {
        draftState.attachments.push({ file, url: previewUrl });
      }),
    );
  };

  removeAttachment = (removedAttachment) => {
    URL.revokeObjectURL(removedAttachment.url);

    this.setState(
      produce((draftState: Draft<MessageThreadInputState>) => {
        draftState.attachments = draftState.attachments.filter(
          (attachment) => attachment.file !== removedAttachment.file,
        );
      }),
    );
  };

  userTyping = () => {
    const { conversationId } = this.props;

    if (!conversationId) return;

    kustomerCore?.sendTypingActivity({ conversationId, typing: true });
  };

  userStoppedTyping = () => {
    const { conversationId } = this.props;

    if (!conversationId) return;

    kustomerCore?.sendTypingActivity({ conversationId, typing: false });
  };

  handleChange = (e) => {
    const { conversationId } = this.props;
    const body = e.target.value;

    if (conversationId) {
      this.debouncedUserTyping();
      this.debouncedUserStoppedTyping();
    }

    this.setState(
      produce((draftState: Draft<MessageThreadInputState>) => {
        draftState.body = body;
      }),
      this.resetTextAreaSize,
    );
  };

  handleSubmit = () => {
    const { body, attachments } = this.state;
    const { onSubmit, isSendDisabled } = this.props;

    if (isSendDisabled) return;

    if (body || attachments.length) {
      onSubmit({
        body,
        attachments,
      });

      this.setState(
        produce((draftState: Draft<MessageThreadInputState>) => {
          draftState.body = '';
          draftState.attachments = [];
          draftState.errorMessage = undefined;
        }),
        this.resetTextAreaSize,
      );
    }
  };

  renderAttachmentsPreview() {
    const { attachments } = this.state;

    if (!attachments.length) {
      return null;
    }

    return (
      <AttachmentPreviews
        attachments={attachments}
        removeAttachment={this.removeAttachment}
      />
    );
  }

  render(
    { isInputHidden, isSendDisabled }: MessageThreadInputProps,
    { body, attachments, errorMessage }: MessageThreadInputState,
  ) {
    const sendAMessage = ((
      <Text id="messageThreadInputSendAMessage">
        {messages.messageThreadInputSendAMessage}
      </Text>
    ) as unknown) as string;

    return (
      <div
        class={classNames(styles.messageInput, {
          [styles.hasError]: !!errorMessage,
          [styles.isInputHidden]: isInputHidden,
        })}
      >
        {this.renderAttachmentsPreview()}
        <div className={styles.lowerSection}>
          <div class={styles.attachmentButtonWrapper}>
            <AttachmentButton
              addAttachment={this.addAttachment}
              attachments={attachments}
              showError={this.showError}
            />
          </div>
          <Localizer>
            <TextArea
              id={TEXT_AREA_ID}
              className={styles.textArea}
              ariaLabel={sendAMessage}
              placeholder={errorMessage || sendAMessage}
              onChange={this.handleChange}
              onEnter={this.handleSubmit}
              value={body}
            />
          </Localizer>
          <div class={styles.buttonWrapper}>
            <Localizer>
              <Button
                variant="primary"
                className={styles.submitButton}
                ariaLabel={
                  ((
                    <Text id="submitButtonAriaLabel">
                      {messages.submitButtonAriaLabel}
                    </Text>
                  ) as unknown) as string
                }
                onClick={this.handleSubmit}
                disabled={(!body && !attachments.length) || isSendDisabled}
              >
                <SendIcon className={styles.sendIcon} />
              </Button>
            </Localizer>
          </div>
        </div>
      </div>
    );
  }
}

export default MessageThreadInput;
